import { useCurrentUserId } from '@framework/auth';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelSharingUserRoleType } from '@view-model/domain/view-model';
import type { WorkspaceEntity, WorkspaceSetting } from '@workspace/domain/workspace';
import type { ViewModelIndividualPermissionRoleJSON, WorkspaceMemberRoleJSON } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { useEffect, useState } from 'react';

type SnapshotData = {
  workspaceMemberRole: WorkspaceMemberRoleJSON | null;
  workspaceSetting: WorkspaceSetting;
  sharingUserRole: ViewModelSharingUserRoleType;
  groupEnableIndividualViewModelPermissions: boolean;
  individualPermissionRole: ViewModelIndividualPermissionRoleJSON | null;
};

const checkIfEditableUser = (
  snapshotData: SnapshotData,
  resultDispatcher: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const {
    workspaceMemberRole,
    workspaceSetting,
    sharingUserRole,
    groupEnableIndividualViewModelPermissions,
    individualPermissionRole,
  } = snapshotData;

  // ビューモデルが所属するワークスペースに対して権限（編集者）が設定されている
  const isEditableWorkspaceMemberRole = !!workspaceMemberRole && ['admin', 'editor'].includes(workspaceMemberRole);

  // URL共有ビューモデルに対して権限（編集者）が設定されている
  const viewModelSharingUserRoleType = ViewModelSharingUserRoleType.load(sharingUserRole);
  const isEditorOfSharedViewModel =
    workspaceSetting.isViewModelURLShareable && viewModelSharingUserRoleType === ViewModelSharingUserRoleType.Editor;

  // ビューモデルに対して個別権限（編集者）が設定されている
  const isIndividuallyPermittedEditor =
    groupEnableIndividualViewModelPermissions &&
    workspaceSetting.enableIndividualViewModelPermissions &&
    individualPermissionRole === 'editor';

  resultDispatcher(isEditableWorkspaceMemberRole || isEditorOfSharedViewModel || isIndividuallyPermittedEditor);
};

const useSnapshotData = (workspace: WorkspaceEntity, viewModel: ViewModelEntity): SnapshotData => {
  const currentUserId = useCurrentUserId();
  if (currentUserId === null) {
    throw new Error('Not logged in');
  }

  const [workspaceMemberRole] = useSnapshot<WorkspaceMemberRoleJSON | null>({
    path: RTDBPath.Workspace.memberRolePath(workspace.id, currentUserId),
    load: ({ snapshot }) => (snapshot.exists() ? snapshot.val() : null),
    skip: typeof workspace.id !== 'string',
  });

  const [groupEnableIndividualViewModelPermissions] = useSnapshot<boolean>({
    path: RTDBPath.Group.enableIndividualViewModelPermissionsPath(workspace.groupId),
    load: ({ snapshot }) => (snapshot.exists() ? snapshot.val() : false),
    skip: typeof workspace.groupId !== 'string',
  });

  const [individualPermissionRole] = useSnapshot<ViewModelIndividualPermissionRoleJSON | null>({
    path: RTDBPath.ViewModel.individualPermissionPath(viewModel.id, currentUserId),
    load: ({ snapshot }) => (snapshot.exists() ? snapshot.val() : null),
    skip: typeof viewModel.id !== 'string',
  });

  return {
    workspaceMemberRole,
    workspaceSetting: workspace.setting,
    sharingUserRole: viewModel.sharingUserRole,
    groupEnableIndividualViewModelPermissions: groupEnableIndividualViewModelPermissions ?? false,
    individualPermissionRole,
  };
};

/**
 * ログイン中のユーザーが指定したビューモデルに対して編集権限を持つかどうかをチェックする
 *
 * @param workspace
 * @param viewModel
 * @returns
 */
export const useViewModelEditable = (workspace: WorkspaceEntity, viewModel: ViewModelEntity) => {
  const [isEditableUser, setEditableUser] = useState<boolean>(false);
  const snapshotData = useSnapshotData(workspace, viewModel);

  useEffect(() => {
    checkIfEditableUser(snapshotData, setEditableUser);
  }, [snapshotData]);

  return {
    isEditableUser,
  };
};
